import React, { useState, useEffect } from 'react';
import { Form, Input, Select, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { competitorAPI } from '../../services/api';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const { Option } = Select;

const CompetitorEdit: React.FC = () => {
    const { skuId, id } = useParams<{ skuId: string; id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<any>({
        onSave: async (data) => {
            if (isNew) {
                const result = await competitorAPI.create({
                    ...data,
                    sku: parseInt(skuId!),
                    first_seen: dayjs().format('YYYY-MM-DD'),
                    last_seen: dayjs().format('YYYY-MM-DD'),
                });
                message.success('竞品添加成功');
                navigate(`/operation-strategy/${skuId}/competitors`);
            } else {
                await competitorAPI.update(parseInt(id), data);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        if (!isNew) {
            fetchCompetitor();
        } else {
            form.setFieldsValue({ threat_level: 'medium' });
        }
    }, [id]);

    const fetchCompetitor = async () => {
        setLoading(true);
        try {
            const res = await competitorAPI.get(parseInt(id!));
            form.setFieldsValue(res.data);
        } catch (error) {
            message.error('加载失败');
            navigate(`/operation-strategy/${skuId}/competitors`);
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            await competitorAPI.create({
                ...values,
                sku: parseInt(skuId!),
                first_seen: dayjs().format('YYYY-MM-DD'),
                last_seen: dayjs().format('YYYY-MM-DD'),
            });
            message.success('竞品添加成功');
            navigate(`/operation-strategy/${skuId}/competitors`);
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? '添加竞品' : '编辑竞品'}
            breadcrumbs={[
                { title: '运营策略', path: '/operation-strategy' },
                { title: 'SKU', path: `/operation-strategy/${skuId}` },
                { title: '竞品追踪', path: `/operation-strategy/${skuId}/competitors` },
                { title: isNew ? '添加' : '编辑' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                <Form.Item name="asin" label="ASIN" rules={[{ required: true, message: '请输入ASIN' }]}>
                    <Input placeholder="输入竞品ASIN" />
                </Form.Item>
                <Form.Item name="threat_level" label="威胁等级">
                    <Select>
                        <Option value="low">低</Option>
                        <Option value="medium">中</Option>
                        <Option value="high">高</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="notes" label="备注">
                    <Input.TextArea rows={3} />
                </Form.Item>
            </Form>
        </FormPageLayout>
    );
};

export default CompetitorEdit;
